import {
  Component,
  HostBinding,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastrService } from 'ngx-toastr';
import { MsgBoxType, UiAlertComponent } from '../ui-alert/ui-alert.component';
import { ViewportService } from '../../services/viewport.service';

const SWIPE_DISMISS_THRESHOLD_PX = 60;
const EXIT_ANIMATION_MS = 200;
const COPIED_FEEDBACK_MS = 2000;

@Component({
  selector: 'ui-custom-toast',
  templateUrl: './ui-custom-toast.component.html',
  styleUrls: ['./ui-custom-toast.component.scss'],
  imports: [UiAlertComponent, NgIf, NgSwitch, NgSwitchCase],
})
export class UiCustomToastComponent implements OnInit {
  @Input({ required: true }) public message!: string;
  @Input({ required: true }) public alertType!: MsgBoxType;
  @Input() public title: string | undefined;
  /**
   * Set from parent
   */
  public toastId!: number;
  /**
   * Set from parent
   */
  public closeTimeout!: ReturnType<typeof setTimeout>;

  public readonly alertTypes = MsgBoxType;
  public copied = false;

  @HostBinding('class.is-mobile') public get mobileClass(): boolean {
    return this.viewport.isMobile();
  }
  @HostBinding('class.leaving') public leaving = false;
  @HostBinding('style.transform') public swipeTransform: string | null = null;
  @HostBinding('style.transition') public swipeTransition: string | null = null;

  private dragStartY: number | null = null;
  private dragDelta = 0;

  constructor(
    protected toastrService: ToastrService,
    protected readonly viewport: ViewportService,
    private readonly clipboard: Clipboard,
  ) {}

  public ngOnInit(): void {
    this.startTimeout();
  }

  public copy(): void {
    clearTimeout(this.closeTimeout);
    if (this.clipboard.copy(this.message ?? '')) {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, COPIED_FEEDBACK_MS);
    }
    this.startTimeout();
  }

  public action(): void {
    if (this.viewport.isMobile() && !this.leaving) {
      this.swipeTransform = null;
      this.swipeTransition = null;
      this.leaving = true;
      clearTimeout(this.closeTimeout);
      setTimeout(
        () => this.toastrService.remove(this.toastId),
        EXIT_ANIMATION_MS,
      );
      return;
    }
    this.toastrService.remove(this.toastId);
  }

  public mouseover(): void {
    clearTimeout(this.closeTimeout);
  }

  public mouseout(): void {
    this.startTimeout();
  }

  public startTimeout(): void {
    this.closeTimeout = setTimeout(() => {
      this.action();
    }, 5000);
  }

  @HostListener('touchstart', ['$event'])
  public onTouchStart(event: TouchEvent): void {
    if (!this.viewport.isMobile() || this.leaving) {
      return;
    }
    this.dragStartY = event.touches[0].clientY;
    this.dragDelta = 0;
    this.swipeTransition = 'none';
    clearTimeout(this.closeTimeout);
  }

  @HostListener('touchmove', ['$event'])
  public onTouchMove(event: TouchEvent): void {
    if (this.dragStartY === null) {
      return;
    }
    this.dragDelta = Math.max(0, event.touches[0].clientY - this.dragStartY);
    this.swipeTransform = `translateY(${this.dragDelta}px)`;
  }

  @HostListener('touchend')
  public onTouchEnd(): void {
    if (this.dragStartY === null) {
      return;
    }
    this.dragStartY = null;
    this.swipeTransition = null;
    if (this.dragDelta > SWIPE_DISMISS_THRESHOLD_PX) {
      this.action();
      return;
    }
    this.swipeTransform = null;
    this.startTimeout();
  }
}
